"use client";

import Translations from "@translate/components/EmailLogin/shared/EmailLoginTermsCheckbox";
import React, { useCallback } from "react";
import { Icon, ospThemes } from "@ospweb/sdk-ui";
import { cn } from "utils";
import { MessageType } from "@/src/lib/bridge/types";
import { useBridge } from "@/src/lib/bridge/useBridge";
import { I18nUtil } from "@utils/i18n";
import type { EmailLoginVariant } from "../types";

const I18n = I18nUtil.createScoped(Translations);

const CHECKBOX_CLASS: Record<EmailLoginVariant, string> = {
  desktop:
    "relative mt-0.5 flex h-3 w-3 shrink-0 cursor-pointer items-center justify-center rounded-[4px] border border-solid border-border_white_transparent5 bg-background_white_overlay10 transition-colors has-[:checked]:border-background_brand_overlay100 has-[:checked]:bg-background_brand_overlay100 has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-1 has-[:focus-visible]:outline-content_brand_primary",
  popup:
    "relative mt-0.5 flex h-3 w-3 shrink-0 cursor-pointer items-center justify-center rounded-[4px] border border-solid border-border_white_transparent5 bg-background_white_overlay10 transition-colors has-[:checked]:border-background_brand_overlay100 has-[:checked]:bg-background_brand_overlay100 has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-1 has-[:focus-visible]:outline-content_brand_primary",
  mobile:
    "relative flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-2 border border-solid border-border_solid bg-background_solid_neutral_primary transition-colors has-[:checked]:border-background_brand_overlay100 has-[:checked]:bg-background_brand_overlay100 has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-1 has-[:focus-visible]:outline-content_brand_primary",
};

const LABEL_CLASS: Record<EmailLoginVariant, string> = {
  desktop: "body_s text-content_secondary cursor-pointer opacity-80",
  popup: "body_m text-content_secondary cursor-pointer opacity-80",
  mobile: "body_s cursor-pointer text-content_tertiary",
};

const LINK_CLASS: Record<EmailLoginVariant, string> = {
  desktop: "underline cursor-pointer hover:text-content_primary",
  popup: "underline cursor-pointer hover:text-content_primary",
  mobile:
    "cursor-pointer underline decoration-solid underline-offset-2 hover:text-content_primary",
};

interface EmailLoginTermsCheckboxProps {
  variant: EmailLoginVariant;
  agreed: boolean;
  onAgreedChange: (value: boolean) => void;
  termsLink: string;
  privacyLink: string;
  className?: string;
}

export function EmailLoginTermsCheckbox({
  variant,
  agreed,
  onAgreedChange,
  termsLink,
  privacyLink,
  className,
}: EmailLoginTermsCheckboxProps) {
  const { send, isInApp } = useBridge();

  const openExternalUrl = useCallback(
    (url: string) => {
      if (isInApp) {
        send(MessageType.OPEN_URL_EXTERNAL, { url });
        return;
      }
      window.open(url, "_blank");
    },
    [isInApp, send],
  );

  return (
    <div className={cn("flex items-start gap-2", className)}>
      <label className={CHECKBOX_CLASS[variant]}>
        <input
          type="checkbox"
          id="email-login-agree"
          checked={agreed}
          onChange={(e) => onAgreedChange(e.target.checked)}
          className="sr-only"
          aria-describedby="email-login-terms"
        />
        {agreed && (
          <Icon
            name="check"
            size={12}
            color={ospThemes.colors.content_inverse_primary}
            className="pointer-events-none"
          />
        )}
      </label>
      <label
        id="email-login-terms"
        htmlFor="email-login-agree"
        className={LABEL_CLASS[variant]}
      >
        {I18n.t("By continuing, you agree to accept the")}{" "}
        <span
          data-openreplay-label={`EmailLogin · EmailInputStep · span | ${I18n.t("Terms of Use")}`}
          className={LINK_CLASS[variant]}
          onClick={() => openExternalUrl(termsLink)}
          onKeyDown={(e) => e.key === "Enter" && openExternalUrl(termsLink)}
          role="button"
          tabIndex={0}
        >
          {I18n.t("Terms of Use")}
        </span>{" "}
        {I18n.t("and")}{" "}
        <span
          data-openreplay-label={`EmailLogin · EmailInputStep · span | ${I18n.t("Privacy Policy")}`}
          className={LINK_CLASS[variant]}
          onClick={() => openExternalUrl(privacyLink)}
          onKeyDown={(e) => e.key === "Enter" && openExternalUrl(privacyLink)}
          role="button"
          tabIndex={0}
        >
          {I18n.t("Privacy Policy")}
        </span>
      </label>
    </div>
  );
}
